import React, { forwardRef, PropsWithChildren, ReactElement } from 'react';
import {
  CreatableTagsAutocomplete,
  DateInput,
  Input,
  OptionsObject,
  PhoneNumberInput,
  Select,
  TextArea,
  TimezoneSelect,
} from '@app/form';
import { ParticipantField, ParticipantFieldType, usePanel } from '@app/api';
import { getIncomes } from './incomes';
import { useFormikContext } from 'formik';
import { SelectGeolocation } from '../../form/src/Select/SelectGeolocation';

export interface FormikComponentProps {
  label?: string;
  name?: string;
  placeholder?: string;
  opaquePlaceholder?: boolean;
  small?: boolean;
  noMargin?: boolean;
  field: ParticipantField;
  type?: string;
  panelId?: string;
  options?: OptionsObject[];
  ref?: any;
  isMulti?: boolean;
  disabled?: boolean;
}

interface FormInputType {
  (props: PropsWithChildren<FormikComponentProps>, context?: any): ReactElement<
    any,
    any
  > | null;
}

const InputAdapter = forwardRef(
  ({ field, panelId: _, ...props }: FormikComponentProps, ref) => (
    <Input name={field.name} {...props} ref={ref} />
  ),
) as FormInputType;

const EmailAdapter = forwardRef((props: FormikComponentProps, ref) => (
  <InputAdapter {...props} type="email" ref={ref} />
)) as FormInputType;

const NumberAdapter = forwardRef((props: FormikComponentProps, ref) => (
  <InputAdapter {...props} type="number" ref={ref} />
)) as FormInputType;

const TextAreaAdapter = ({
  field,
  panelId: _,
  ...props
}: FormikComponentProps) => <TextArea name={field.name} {...props} />;

const SelectAdapter = ({
  field,
  panelId: _,
  ...props
}: FormikComponentProps) => (
  <Select name={field.name} options={field.choices} isClearable {...props} />
);

const MultiSelectAdapter = (props: FormikComponentProps) => (
  <SelectAdapter isMulti {...props} />
);

const TagsAdapter = ({ field, panelId, ...props }: FormikComponentProps) => {
  const { data: panel } = usePanel(panelId);
  return (
    <CreatableTagsAutocomplete
      name={field.name}
      {...props}
      isMulti={true}
      options={panel?.tags || []}
    />
  );
};

const DateAdapter = ({ field, panelId: _, ...props }: FormikComponentProps) => (
  <DateInput id={field.name} name={field.name} {...props} />
);

const IncomeAdapter = (props: FormikComponentProps) => {
  const formik = useFormikContext<any>();
  const countries =
    formik && formik.values && formik.values.countryKey
      ? [formik.values.countryKey]
      : [];
  return <SelectAdapter options={getIncomes(countries)} {...props} />;
};

const TimezoneSelectAdapter = ({
  field,
  panelId: _,
  ...props
}: FormikComponentProps) => (
  <TimezoneSelect name={field.name} isClearable {...props} />
);

const InternationalPhoneNumberAdapter = ({
  field,
  panelId: _,
  ...props
}: FormikComponentProps) => {
  return <PhoneNumberInput name={field.name} label={props.label} />;
};

const InputGeolocation = ({ field, ...props }: FormikComponentProps) => {
  return <SelectGeolocation {...props} name={field.name} label={props.label} />;
};

const textFieldInputMap: Record<ParticipantFieldType, FormInputType | null> = {
  [ParticipantFieldType.EMAIL]: EmailAdapter,
  [ParticipantFieldType.PHONE_NUMBER]: InternationalPhoneNumberAdapter,
  [ParticipantFieldType.SINGLE_CHOICE]: SelectAdapter,
  [ParticipantFieldType.MULTIPLE_CHOICE]: MultiSelectAdapter,
  [ParticipantFieldType.TAGS]: TagsAdapter,
  [ParticipantFieldType.SHORT_TEXT]: InputAdapter,
  [ParticipantFieldType.LONG_TEXT]: TextAreaAdapter,
  [ParticipantFieldType.DATE]: DateAdapter,
  [ParticipantFieldType.NUMBER]: NumberAdapter,
  [ParticipantFieldType.GEOLOCATION]: InputGeolocation,
  [ParticipantFieldType.INCOME]: IncomeAdapter,
  [ParticipantFieldType.TIMEZONE]: TimezoneSelectAdapter,
  [ParticipantFieldType.LOCALES]: MultiSelectAdapter,
  [ParticipantFieldType.BOOLEAN]: null,
};

export const getParticipantFieldInput = (
  field: ParticipantField,
): FormInputType | null => {
  return textFieldInputMap[field.type];
};
